import { useMemo } from "react";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { IAvailableTimeSlot } from "../models/IAppointmentWizard";

interface IBulkTimeSlotResponse {
  [dateKey: string]: {
    startTime: string;
    endTime: string;
    displayTime: string;
    isAvailable: boolean;
  }[];
}

export interface IDateAvailability {
  date: Date;
  dateKey: string;
  hasSlots: boolean;
  timeSlots: IAvailableTimeSlot[];
}

/**
 * Hook to fetch 2-week bulk availability for an employee and service
 */
export const useBulkAppointmentAvailability = (
  employeeId: number | undefined,
  serviceId: number | undefined,
  startDate: Date | undefined,
  studioId: number | undefined
) => {
  const startDateString = startDate
    ? startDate.toISOString().split("T")[0]
    : undefined;

  const {
    data: bulkData,
    isLoading,
    error,
  } = useApiQuery<IBulkTimeSlotResponse>({
    queryKey: [
      endpointsMetadata.appointments.getBulkAvailableTimeSlots,
      employeeId,
      serviceId,
      startDateString,
      studioId,
    ],
    endpoint: `${endpointsMetadata.appointments.getBulkAvailableTimeSlots}?employeeId=${employeeId}&serviceId=${serviceId}&startDate=${startDateString}&studioId=${studioId}`,
    enabled:
      !!employeeId &&
      employeeId !== -1 &&
      !!serviceId &&
      !!startDateString &&
      !!studioId,
    globalIsLoading: false,
  });

  // Convert backend data to frontend format with availability info
  // The backend returns only the days the user is allowed to see based on visibility settings
  const dateAvailabilityMap = useMemo((): Map<string, IDateAvailability> => {
    if (!bulkData) return new Map();

    const availabilityMap = new Map<string, IDateAvailability>();

    // Iterate over the days returned by the backend (respects visibility limits)
    const dateKeys = Object.keys(bulkData).sort();

    for (const dateKey of dateKeys) {
      // Create date from the string key to ensure consistency
      const currentDate = new Date(dateKey + "T12:00:00");

      const backendSlots = bulkData[dateKey] || [];
      const timeSlots: IAvailableTimeSlot[] = backendSlots.map((slot) => ({
        time: slot.displayTime,
        isAvailable: slot.isAvailable,
        employeeId: employeeId || 0,
      }));

      availabilityMap.set(dateKey, {
        date: currentDate,
        dateKey,
        hasSlots: timeSlots.length > 0,
        timeSlots,
      });
    }

    return availabilityMap;
  }, [bulkData, employeeId]);

  // Helper function to get availability for a specific date
  const getDateAvailability = (date: Date): IDateAvailability | undefined => {
    const dateKey = date.toISOString().split("T")[0];
    return dateAvailabilityMap.get(dateKey);
  };

  // Helper function to check if a date has available slots
  const hasAvailableSlots = (date: Date): boolean => {
    const availability = getDateAvailability(date);
    return (
      (availability?.hasSlots &&
        availability.timeSlots.some((slot) => slot.isAvailable)) ||
      false
    );
  };

  // Get the number of visible days (determined by backend based on visibility settings)
  const visibleDaysCount = dateAvailabilityMap.size;

  return {
    dateAvailabilityMap,
    getDateAvailability,
    hasAvailableSlots,
    isLoading,
    error,
    hasData: !!bulkData,
    visibleDaysCount,
  };
};
